// The <annots> half of XFDF: comments in, comments out.
//
// This is what makes a review round-trip work — mark a document up
// here, hand the .xfdf to someone in Acrobat, get their copy back and
// see their comments here. The document itself never travels, which is
// the point: two people can annotate the same PDF independently and
// merge the results.
//
// Coordinates. The editor works in fitz page space (origin top-left,
// y DOWN, after /Rotate). XFDF is raw PDF user space (origin
// bottom-left, y UP). Every geometry here is flipped through the page
// height on the way out and back on the way in, which is its own
// inverse — so an export/import cycle is exact. The one thing this
// does not model is a page whose /Rotate is non-zero: Acrobat writes
// such rects in the UNROTATED space, and the page height the UI knows
// is the rotated one. Those pages export at the right size but the
// wrong offset in other readers (they round-trip fine here).
//
// Not every overlay kind has an XFDF equivalent worth writing:
//   - stamps (including signatures) need a base64 PDF appearance
//     stream, which XFDF carries but this does not build;
//   - pending redactions are an editor-local intent, not a comment —
//     exporting one as a comment would misrepresent it as harmless;
//   - links are document structure, and reader support for them in
//     XFDF is too patchy to be worth the mess.
// Those are counted and reported rather than dropped in silence.
import { annotBounds } from "./annot-geometry.ts";
import {
  isMarkup,
  shapeHeads,
  type Annotation,
  type AnnotationReply,
  type AnnotationStatus,
  type DashStyle,
  type HeadStyle,
  type MarkupKind,
  type Quad,
  type ShapeKind,
  type TextAlign,
} from "./protocol.ts";
import { nextAnnotId } from "./state.ts";
import {
  allText,
  childrenNamed,
  encodeXmlText,
  findNode,
  parseXml,
  type XmlNode,
} from "./xfdf.ts";

/** Just enough of a page to flip coordinates through. */
export interface XfdfPage {
  width: number;
  height: number;
}

// ─────────────────────────── primitives ───────────────────────────

/** Three decimals is finer than any PDF renderer resolves, and keeps
 *  the file readable instead of full of floating-point noise. */
function num(n: number): string {
  return String(Math.round(n * 1000) / 1000);
}

function nums(s: string | undefined): number[] {
  if (!s) return [];
  return s
    .split(/[,;\s]+/)
    .map((t) => Number.parseFloat(t))
    .filter((n) => Number.isFinite(n));
}

/** CSS colour → "#rrggbb", or null if it isn't a plain hex colour.
 *  The editor only ever stores hex, so anything else came from a file. */
function normHex(c: string | undefined): string | null {
  if (!c) return null;
  const m = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(c.trim());
  if (!m) return null;
  const h = m[1]!.toLowerCase();
  return h.length === 3 ? `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}` : `#${h}`;
}

/** ISO timestamp → PDF date string. Written in UTC: the offset form is
 *  legal too, but UTC makes exports from different machines comparable. */
export function toPdfDate(iso: string | undefined): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const d = new Date(t);
  const p = (n: number, w = 2) => String(n).padStart(w, "0");
  return (
    `D:${p(d.getUTCFullYear(), 4)}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}` +
    `${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}Z00'00'`
  );
}

/** PDF date string → ISO timestamp. Everything after the year is
 *  optional in the spec, and real files do omit trailing parts. */
export function fromPdfDate(raw: string | undefined): string | null {
  if (!raw) return null;
  const m =
    /^\s*(?:D:)?(\d{4})(\d{2})?(\d{2})?(\d{2})?(\d{2})?(\d{2})?(?:(Z|[+-])(\d{2})'?(\d{2})?'?)?/.exec(
      raw
    );
  if (!m) return null;
  const [, y, mo, da, hh, mi, ss, sign, oh, om] = m;
  const offset =
    !sign || sign === "Z"
      ? 0
      : (sign === "-" ? -1 : 1) * (Number(oh ?? 0) * 60 + Number(om ?? 0));
  const t = Date.UTC(
    Number(y),
    Number(mo ?? 1) - 1,
    Number(da ?? 1),
    Number(hh ?? 0),
    Number(mi ?? 0),
    Number(ss ?? 0)
  );
  if (!Number.isFinite(t)) return null;
  return new Date(t - offset * 60_000).toISOString();
}

// ─────────────────────────── geometry ───────────────────────────

type Box = [number, number, number, number];

/** fitz box (y down) → XFDF rect string (y up). */
function boxOut(b: Box, h: number): string {
  return `${num(b[0])},${num(h - b[3])},${num(b[2])},${num(h - b[1])}`;
}

/** XFDF rect string → fitz box, normalised so y0 is the visual top. */
function boxIn(s: string | undefined, h: number): Box | null {
  const v = nums(s);
  if (v.length < 4) return null;
  const [x0, py0, x1, py1] = v as [number, number, number, number];
  return [
    Math.min(x0, x1),
    h - Math.max(py0, py1),
    Math.max(x0, x1),
    h - Math.min(py0, py1),
  ];
}

/** Quad corner order is identical in both spaces (upper-left,
 *  upper-right, lower-left, lower-right) — only y changes sign. */
function quadsOut(quads: readonly Quad[], h: number): string {
  return quads
    .map((q) =>
      [q[0], h - q[1], q[2], h - q[3], q[4], h - q[5], q[6], h - q[7]].map(num).join(",")
    )
    .join(",");
}

function quadsIn(s: string | undefined, h: number): Quad[] {
  const v = nums(s);
  const out: Quad[] = [];
  for (let i = 0; i + 7 < v.length; i += 8) {
    out.push([
      v[i]!, h - v[i + 1]!,
      v[i + 2]!, h - v[i + 3]!,
      v[i + 4]!, h - v[i + 5]!,
      v[i + 6]!, h - v[i + 7]!,
    ]);
  }
  return out;
}

function quadOfBox(b: Box): Quad {
  return [b[0], b[1], b[2], b[1], b[0], b[3], b[2], b[3]];
}

// ─────────────────────────── enum mappings ───────────────────────────

const MARKUP_EL: Record<MarkupKind, string> = {
  highlight: "highlight",
  underline: "underline",
  strikeout: "strikeout",
  squiggly: "squiggly",
};
const MARKUP_KIND: Record<string, MarkupKind> = {
  highlight: "highlight",
  underline: "underline",
  strikeout: "strikeout",
  squiggly: "squiggly",
};

const HEAD_OUT: Record<HeadStyle, string> = {
  none: "None",
  arrow: "ClosedArrow",
  open: "OpenArrow",
  circle: "Circle",
  square: "Square",
  diamond: "Diamond",
};
/** XFDF has more line endings than this editor draws; the extra ones
 *  (Butt, Slash, and the "R"-reversed variants) collapse onto the
 *  nearest thing that can actually be rendered here. */
const HEAD_IN: Record<string, HeadStyle> = {
  none: "none",
  butt: "none",
  slash: "none",
  closedarrow: "arrow",
  rclosedarrow: "arrow",
  openarrow: "open",
  ropenarrow: "open",
  circle: "circle",
  square: "square",
  diamond: "diamond",
};

const ALIGN_OUT: Record<TextAlign, string> = { left: "0", center: "1", right: "2" };
const ALIGN_IN: Record<string, TextAlign> = { "0": "left", "1": "center", "2": "right" };

const STATUS_OUT: Record<AnnotationStatus, string> = {
  accepted: "Accepted",
  rejected: "Rejected",
  completed: "Completed",
  cancelled: "Cancelled",
};
const STATUS_IN: Record<string, AnnotationStatus> = {
  accepted: "accepted",
  rejected: "rejected",
  completed: "completed",
  cancelled: "cancelled",
  none: "accepted",
};

/** Dash pattern → XFDF `dashes`, expressed in multiples of the stroke
 *  width so it survives a width change the way the on-screen preview
 *  does (see dashArray in annot-geometry). */
function dashesOut(dash: DashStyle | undefined, w: number): string | null {
  if (dash === "dashed") return `${num(w * 3)},${num(w * 2)}`;
  if (dash === "dotted") return `${num(w)},${num(w * 2)}`;
  return null;
}

/** …and back. A dash whose "on" length is around the stroke width is a
 *  dotted line; anything longer reads as dashed. */
function dashIn(node: XmlNode, width: number): DashStyle {
  const on = nums(node.attrs.dashes)[0];
  if (on === undefined || on <= 0) return "solid";
  return on <= Math.max(width, 0.5) * 1.5 ? "dotted" : "dashed";
}

// ─────────────────────────── export ───────────────────────────

/** Kinds that have no XFDF form here, with why, for the report. */
export type UnexportableKind = "stamp" | "redact" | "link";

export interface BuildAnnotXfdfResult {
  xml: string;
  /** Annotations written (replies and status markers not counted). */
  exported: number;
  /** Per-kind count of what had to be left out. */
  skipped: Partial<Record<UnexportableKind, number>>;
}

function attr(name: string, value: string | null | undefined): string {
  return value == null || value === "" ? "" : ` ${name}="${encodeXmlText(value)}"`;
}

function contentsEl(value: string): string {
  return value ? `\n      <contents>${encodeXmlText(value)}</contents>` : "";
}

/** page/rect/name/title/date — the attributes every annotation carries. */
function common(a: Annotation, box: Box, h: number): string {
  const meta = a as { author?: string; modified?: string };
  const date = toPdfDate(meta.modified);
  return (
    ` page="${a.pageIndex}" rect="${boxOut(box, h)}"` +
    attr("name", a.id) +
    attr("title", meta.author) +
    attr("date", date) +
    attr("creationdate", date)
  );
}

/** The `<text>` elements that carry a comment thread and a review
 *  state. Both are ordinary Text annotations pointing back at the
 *  parent with /IRT — the convention Acrobat's comment pane reads, and
 *  the same shape the worker already writes into saved PDFs.
 *
 *  They are SIBLINGS of the annotation they reply to, not children:
 *  <annots> is a flat list and the thread is expressed purely by
 *  `inreplyto`. Nesting them makes a file every other reader ignores. */
function threadEls(a: Annotation, box: Box, h: number): string[] {
  const meta = a as { replies?: AnnotationReply[]; status?: AnnotationStatus };
  const rect = boxOut(box, h);
  const out: string[] = [];
  (meta.replies ?? []).forEach((r, i) => {
    const date = toPdfDate(r.date);
    out.push(
      `    <text page="${a.pageIndex}" rect="${rect}"` +
        attr("name", `${a.id}r${i + 1}`) +
        attr("title", r.author) +
        attr("date", date) +
        attr("creationdate", date) +
        ` inreplyto="${encodeXmlText(a.id)}" replyType="R" icon="Comment">` +
        `\n      <contents>${encodeXmlText(r.text)}</contents>\n    </text>`
    );
  });
  if (meta.status) {
    out.push(
      `    <text page="${a.pageIndex}" rect="${rect}"` +
        attr("name", `${a.id}s`) +
        attr("title", (a as { author?: string }).author) +
        ` inreplyto="${encodeXmlText(a.id)}" replyType="Group"` +
        ` statemodel="Review" state="${STATUS_OUT[meta.status]}">` +
        `\n      <contents>${encodeXmlText(STATUS_OUT[meta.status])}</contents>\n    </text>`
    );
  }
  return out;
}

/** One annotation as an XFDF element, or null if it has no XFDF form. */
function annotEl(a: Annotation, page: XfdfPage): string | null {
  const h = page.height;

  const box = annotBounds(a);

  if (isMarkup(a)) {
    const el = MARKUP_EL[a.kind];
    return (
      `    <${el}${common(a, box, h)}` +
      attr("color", normHex(a.color)) +
      ` coords="${quadsOut(a.quads, h)}">` +
      contentsEl(a.contents ?? "") +
      `\n    </${el}>`
    );
  }

  switch (a.kind) {
    case "text": {
      const rgb = normHex(a.color) ?? "#000000";
      const da = [1, 3, 5]
        .map((i) => num(Number.parseInt(rgb.slice(i, i + 2), 16) / 255))
        .join(" ");
      // The text colour lives in the default-appearance string, NOT in
      // `color` — on a FreeText, `color` is the border, and this box
      // deliberately has none. Readers that only look at the DA (which
      // is most of them, for text) still get the right colour.
      return (
        `    <freetext${common(a, box, h)} width="0"` +
        ` justification="${ALIGN_OUT[a.align ?? "left"]}"` +
        (a.opacity != null && a.opacity < 1 ? ` opacity="${num(a.opacity)}"` : "") +
        `>` +
        `\n      <defaultappearance>${da} rg /Helv ${num(a.size)} Tf</defaultappearance>` +
        contentsEl(a.value) +
        `\n    </freetext>`
      );
    }
    case "ink": {
      const pts: string[] = [];
      for (let i = 0; i + 1 < a.points.length; i += 2) {
        pts.push(`${num(a.points[i]!)},${num(h - a.points[i + 1]!)}`);
      }
      return (
        `    <ink${common(a, box, h)}` +
        attr("color", normHex(a.color)) +
        ` width="${num(a.strokeWidth)}">` +
        `\n      <inklist>\n        <gesture>${pts.join(";")}</gesture>\n      </inklist>` +
        contentsEl(a.contents ?? "") +
        `\n    </ink>`
      );
    }
    case "shape": {
      const dashes = dashesOut(a.dash, a.strokeWidth);
      const style = dashes ? ` style="dash" dashes="${dashes}"` : "";
      if (a.shape === "line" || a.shape === "arrow") {
        const heads = shapeHeads(a);
        return (
          `    <line${common(a, box, h)}` +
          attr("color", normHex(a.color)) +
          ` width="${num(a.strokeWidth)}"` +
          ` start="${num(a.x1)},${num(h - a.y1)}" end="${num(a.x2)},${num(h - a.y2)}"` +
          ` head="${HEAD_OUT[heads.start]}" tail="${HEAD_OUT[heads.end]}"` +
          style +
          `>` +
          contentsEl(a.contents ?? "") +
          `\n    </line>`
        );
      }
      const el = a.shape === "rect" ? "square" : "circle";
      return (
        `    <${el}${common(a, box, h)}` +
        attr("color", normHex(a.color)) +
        attr("interior-color", normHex(a.fill ?? undefined)) +
        ` width="${num(a.strokeWidth)}"` +
        style +
        `>` +
        contentsEl(a.contents ?? "") +
        `\n    </${el}>`
      );
    }
    case "note": {
      return (
        `    <text${common(a, box, h)}` +
        attr("color", normHex(a.color)) +
        ` icon="Comment">` +
        contentsEl(a.value) +
        `\n    </text>`
      );
    }
    default:
      return null;
  }
}

/** Write the annotation overlay out as XFDF. Annotations on pages the
 *  caller didn't describe are skipped — there is no height to flip
 *  them through. */
export function buildAnnotXfdf(
  annots: readonly Annotation[],
  pages: readonly XfdfPage[],
  href?: string
): BuildAnnotXfdfResult {
  const els: string[] = [];
  const skipped: Partial<Record<UnexportableKind, number>> = {};
  for (const a of annots) {
    const page = pages[a.pageIndex];
    if (!page) continue;
    const el = annotEl(a, page);
    if (el) {
      els.push(el, ...threadEls(a, annotBounds(a), page.height));
    } else {
      const k = a.kind as UnexportableKind;
      skipped[k] = (skipped[k] ?? 0) + 1;
    }
  }
  const xml =
    `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve">\n` +
    (href ? `  <f href="${encodeXmlText(href)}"/>\n` : "") +
    `  <annots>\n${els.join("\n")}${els.length ? "\n" : ""}  </annots>\n` +
    `</xfdf>\n`;
  return { xml, exported: els.length, skipped };
}

// ─────────────────────────── import ───────────────────────────

export interface ParseAnnotXfdfResult {
  annots: Annotation[];
  /** Elements inside <annots> whose type this editor can't represent. */
  unsupported: number;
  /** Annotations naming a page this document doesn't have. */
  offPage: number;
}

/** Colour out of a `<defaultappearance>` DA string ("r g b rg …"). */
function daColor(da: string): string | null {
  const m = /(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s+rg/.exec(da);
  if (m) {
    const hex = [1, 2, 3]
      .map((i) => {
        const v = Math.round(Math.min(1, Math.max(0, Number(m[i]))) * 255);
        return v.toString(16).padStart(2, "0");
      })
      .join("");
    return `#${hex}`;
  }
  const g = /(-?[\d.]+)\s+g\b/.exec(da);
  if (g) {
    const v = Math.round(Math.min(1, Math.max(0, Number(g[1]))) * 255)
      .toString(16)
      .padStart(2, "0");
    return `#${v}${v}${v}`;
  }
  return null;
}

/** Font size out of a DA string ("/Helv 12 Tf"). 0 means "auto-size" in
 *  a PDF appearance, which this editor has no equivalent for. */
function daSize(da: string): number | null {
  const m = /\/\S+\s+([\d.]+)\s+Tf/.exec(da);
  const n = m ? Number(m[1]) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

function childText(node: XmlNode, name: string): string | null {
  const c = childrenNamed(node, name)[0];
  return c ? allText(c) : null;
}

/** Contents of an annotation: plain `<contents>` if present, otherwise
 *  the rich-text variant flattened to its text. */
function contentsOf(node: XmlNode): string {
  const plain = childText(node, "contents");
  if (plain != null && plain.trim() !== "") return plain;
  const rich = childrenNamed(node, "contents-richtext")[0];
  return rich ? allText(rich).trim() : (plain ?? "");
}

/** `<contents>` as the annotation's own comment — for every kind whose
 *  words are not already its visible value. Absent rather than empty so
 *  a round-trip doesn't grow a key the original didn't have. */
function contentsMeta(node: XmlNode): { contents?: string } {
  const text = contentsOf(node).trim();
  return text ? { contents: text } : {};
}

function metaOf(node: XmlNode): {
  author?: string;
  modified?: string;
} {
  const out: { author?: string; modified?: string } = {};
  if (node.attrs.title) out.author = node.attrs.title;
  const date = fromPdfDate(node.attrs.date ?? node.attrs.creationdate);
  if (date) out.modified = date;
  return out;
}

function widthOf(node: XmlNode, fallback: number): number {
  const w = Number.parseFloat(node.attrs.width ?? "");
  return Number.isFinite(w) && w > 0 ? w : fallback;
}

/** One XFDF element → one overlay annotation, or null if the element is
 *  a type this editor doesn't draw. */
function annotFromEl(node: XmlNode, page: XfdfPage, id: string): Annotation | null {
  const h = page.height;
  const meta = metaOf(node);
  const box = boxIn(node.attrs.rect, h);
  const pageIndex = Number.parseInt(node.attrs.page ?? "0", 10) || 0;

  const markup = MARKUP_KIND[node.name];
  if (markup) {
    const quads = quadsIn(node.attrs.coords, h);
    // A markup with no coords is degenerate, but its rect still says
    // where it was — one quad covering the box beats dropping it.
    const use = quads.length ? quads : box ? [quadOfBox(box)] : [];
    if (!use.length) return null;
    return {
      id,
      kind: markup,
      pageIndex,
      color: normHex(node.attrs.color) ?? "#ffe066",
      quads: use,
      ...contentsMeta(node),
      ...meta,
    };
  }

  switch (node.name) {
    case "freetext": {
      if (!box) return null;
      const da = childText(node, "defaultappearance") ?? "";
      const opacity = Number.parseFloat(node.attrs.opacity ?? "");
      return {
        id,
        kind: "text",
        pageIndex,
        x: box[0],
        y: box[1],
        width: Math.max(box[2] - box[0], 1),
        height: Math.max(box[3] - box[1], 1),
        size: daSize(da) ?? 12,
        color: daColor(da) ?? normHex(node.attrs.color) ?? "#000000",
        value: contentsOf(node),
        align: ALIGN_IN[node.attrs.justification ?? "0"] ?? "left",
        ...(Number.isFinite(opacity) && opacity < 1 ? { opacity } : {}),
        ...meta,
      };
    }
    case "ink": {
      // The spec allows several <gesture> strokes per annotation; this
      // model is one stroke per annotation, so they concatenate. Joining
      // them can add a connecting segment, but losing strokes is worse.
      const list = childrenNamed(node, "inklist")[0];
      const gestures = list ? childrenNamed(list, "gesture") : [];
      const points: number[] = [];
      for (const g of gestures) {
        const v = nums(allText(g));
        for (let i = 0; i + 1 < v.length; i += 2) points.push(v[i]!, h - v[i + 1]!);
      }
      if (points.length < 4) return null;
      return {
        id,
        kind: "ink",
        pageIndex,
        color: normHex(node.attrs.color) ?? "#e03131",
        strokeWidth: widthOf(node, 2),
        points,
        ...contentsMeta(node),
        ...meta,
      };
    }
    case "square":
    case "circle": {
      if (!box) return null;
      const strokeWidth = widthOf(node, 1);
      return {
        id,
        kind: "shape",
        shape: node.name === "square" ? "rect" : "ellipse",
        pageIndex,
        x1: box[0],
        y1: box[1],
        x2: box[2],
        y2: box[3],
        color: normHex(node.attrs.color) ?? "#1971c2",
        strokeWidth,
        fill: normHex(node.attrs["interior-color"]),
        dash: dashIn(node, strokeWidth),
        ...contentsMeta(node),
        ...meta,
      };
    }
    case "line": {
      const s = nums(node.attrs.start);
      const e = nums(node.attrs.end);
      // Without endpoints the rect is all there is; its diagonal is the
      // best available guess at the line.
      const p1: [number, number] =
        s.length >= 2 ? [s[0]!, h - s[1]!] : box ? [box[0], box[1]] : [0, 0];
      const p2: [number, number] =
        e.length >= 2 ? [e[0]!, h - e[1]!] : box ? [box[2], box[3]] : [0, 0];
      const headStart = HEAD_IN[(node.attrs.head ?? "None").toLowerCase()] ?? "none";
      const headEnd = HEAD_IN[(node.attrs.tail ?? "None").toLowerCase()] ?? "none";
      const strokeWidth = widthOf(node, 2);
      // "arrow" and "line" differ only in their default decoration, and
      // the heads are explicit here either way — but naming it an arrow
      // when it has one is what the properties bar and the tool chip
      // will show the user.
      const shape: ShapeKind =
        headEnd !== "none" && headStart === "none" ? "arrow" : "line";
      return {
        id,
        kind: "shape",
        shape,
        pageIndex,
        x1: p1[0],
        y1: p1[1],
        x2: p2[0],
        y2: p2[1],
        color: normHex(node.attrs.color) ?? "#1971c2",
        strokeWidth,
        fill: null,
        dash: dashIn(node, strokeWidth),
        headStart,
        headEnd,
        ...contentsMeta(node),
        ...meta,
      };
    }
    case "text": {
      if (!box) return null;
      return {
        id,
        kind: "note",
        pageIndex,
        x: box[0],
        y: box[1],
        color: normHex(node.attrs.color) ?? "#fab005",
        value: contentsOf(node),
        ...meta,
      };
    }
    default:
      return null;
  }
}

/** Read comments out of an XFDF document.
 *
 *  `pages` describes the document they are landing on: an annotation
 *  naming a page that doesn't exist is reported, not clamped onto some
 *  other page. Ids are re-minted rather than taken from the file's
 *  `name` attributes, so importing the same file twice adds two sets of
 *  comments instead of colliding with the first. */
export function parseAnnotXfdf(
  xml: string,
  pages: readonly XfdfPage[],
  makeId: () => string = nextAnnotId
): ParseAnnotXfdfResult {
  const out: ParseAnnotXfdfResult = { annots: [], unsupported: 0, offPage: 0 };
  const root = parseXml(xml);
  const annots = root && findNode(root, "annots");
  if (!annots) return out;

  /** XFDF `name` → the annotation it became, so replies can find it. */
  const byName = new Map<string, Annotation>();
  const deferred: XmlNode[] = [];

  for (const node of annots.children) {
    if (node.name === "popup") continue; // a popup is chrome for its parent
    if (node.attrs.inreplyto) {
      deferred.push(node);
      continue;
    }
    const pageIndex = Number.parseInt(node.attrs.page ?? "0", 10) || 0;
    const page = pages[pageIndex];
    if (!page) {
      // Only count it against the document if it was something we could
      // otherwise have drawn.
      if (MARKUP_KIND[node.name] || KNOWN_ELS.has(node.name)) out.offPage += 1;
      else out.unsupported += 1;
      continue;
    }
    const a = annotFromEl(node, page, makeId());
    if (!a) {
      out.unsupported += 1;
      continue;
    }
    out.annots.push(a);
    if (node.attrs.name) byName.set(node.attrs.name, a);
  }

  // Second pass: /IRT children are replies to, or a review state on,
  // an annotation from the first pass. One that points at nothing we
  // kept is dropped — a reply with no comment to attach to has nowhere
  // to live in this model.
  for (const node of deferred) {
    const parent = byName.get(node.attrs.inreplyto!);
    if (!parent) continue;
    const state = node.attrs.state ?? (node.attrs.statemodel ? "" : undefined);
    if (state !== undefined) {
      const status = STATUS_IN[state.toLowerCase()];
      if (status) (parent as { status?: AnnotationStatus }).status = status;
      continue;
    }
    const text = contentsOf(node);
    if (!text) continue;
    const p = parent as { replies?: AnnotationReply[] };
    p.replies ??= [];
    const reply: AnnotationReply = { text };
    if (node.attrs.title) reply.author = node.attrs.title;
    const date = fromPdfDate(node.attrs.date ?? node.attrs.creationdate);
    if (date) reply.date = date;
    p.replies.push(reply);
  }

  return out;
}

/** Element names this editor can draw — used only to tell "we could
 *  have had this, but the page is missing" from "we never could". */
const KNOWN_ELS = new Set(["freetext", "ink", "square", "circle", "line", "text"]);
